import { apiDelete, apiGet, apiPatch, apiPost, apiPut, getApiUrl, planningPath } from './PlanningApiClient';
import type {
  GiftItem, GiftItemPayload, GiftKind, GiftShare, PublicGiftItem, PublicRegistry, ReserveInput,
} from '../types/GiftRegistry';

/**
 * Gift Registry API. The honorees' calls go through the planning client (signed in, project from the session). The guests' calls
 * (bottom of the file) use a plain fetch: no API key, no sign-in, nothing of the staff session is sent to the public page.
 * Nothing here swallows an error: a screen decides how to say it failed.
 */

const enc = encodeURIComponent;
export type GiftView = 'active' | 'archived' | 'all';
const base = '/gift-registry';

export async function fetchGiftItems(opts: { view?: GiftView; kind?: GiftKind } = {}): Promise<GiftItem[]> {
  const usp = new URLSearchParams();
  if (opts.view && opts.view !== 'active') usp.set('archived', opts.view === 'archived' ? 'true' : 'all');
  if (opts.kind) usp.set('kind', opts.kind);
  const s = usp.toString();
  const res = await apiGet<{ items?: GiftItem[] }>(planningPath(`${base}/items${s ? `?${s}` : ''}`));
  return Array.isArray(res?.items) ? res.items : [];
}

export const fetchGiftItem = (id: string) => apiGet<GiftItem>(planningPath(`${base}/items/${enc(id)}`));

/** The record page is addressed by its number (GR-7); the API finds records by id, so the number is looked up in the project's list. */
export async function fetchGiftItemByRef(ref: number): Promise<GiftItem | null> {
  const items = await fetchGiftItems({ view: 'all' });
  return items.find(i => i.ref_number === ref) ?? null;
}

export const createGiftItem = (payload: GiftItemPayload) => apiPost<GiftItem>(planningPath(`${base}/items`), payload);
export const updateGiftItem = (id: string, payload: GiftItemPayload) => apiPatch<GiftItem>(planningPath(`${base}/items/${enc(id)}`), payload);
export const archiveGiftItem = (id: string) => apiDelete<{ item: GiftItem }>(planningPath(`${base}/items/${enc(id)}`));
export const restoreGiftItem = (id: string) => apiDelete<{ item: GiftItem }>(planningPath(`${base}/items/${enc(id)}?restore=true`));
export const deleteGiftItemForever = (id: string) => apiDelete(planningPath(`${base}/items/${enc(id)}?permanent=true`));
export const removeGiftReservation = (itemId: string, reservationId: string) =>
  apiDelete(planningPath(`${base}/items/${enc(itemId)}/reservations/${enc(reservationId)}`));

export const fetchGiftShare = () => apiGet<GiftShare>(planningPath(`${base}/share`));
export const setGiftShareEnabled = (enabled: boolean) => apiPut<GiftShare>(planningPath(`${base}/share`), { enabled });
export const regenerateGiftShare = () => apiPost<GiftShare>(planningPath(`${base}/share/regenerate`), {});

// ── guests ──

/** A refusal from the public API, in words a guest can read. `remaining` is set when someone reserved first. */
export class PublicRegistryError extends Error {
  constructor(readonly status: number, message: string, readonly remaining: number | null = null) {
    super(message);
    this.name = 'PublicRegistryError';
  }
}

async function publicRequest<T>(path: string, init?: { method: 'POST'; body: unknown }): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${getApiUrl()}${path}`, {
      method: init?.method ?? 'GET',
      headers: init ? { 'Content-Type': 'application/json' } : undefined,
      body: init ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new PublicRegistryError(0, "Couldn't reach the server. Check your connection and try again.");
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string; remaining_quantity?: number } | null;
    const fallback = res.status === 404 ? 'This registry link is not available.'
      : res.status === 429 ? 'Too many requests. Please wait a minute and try again.'
      : 'Something went wrong. Please try again.';
    throw new PublicRegistryError(res.status, typeof body?.error === 'string' && res.status !== 400 ? body.error : fallback,
      typeof body?.remaining_quantity === 'number' ? body.remaining_quantity : null);
  }
  return (await res.json()) as T;
}

export const fetchPublicRegistry = (token: string) => publicRequest<PublicRegistry>(`/public/gift-registry/${enc(token)}`);

export async function reservePublicGift(token: string, itemId: string, input: ReserveInput): Promise<PublicGiftItem> {
  const body: Record<string, unknown> = { guest_name: input.guest_name.trim(), quantity: input.quantity };
  if (input.note && input.note.trim() !== '') body.note = input.note.trim();
  const res = await publicRequest<{ item: PublicGiftItem }>(
    `/public/gift-registry/${enc(token)}/items/${enc(itemId)}/reserve`, { method: 'POST', body });
  return res.item;
}
